import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Phone, 
  Calendar, 
  Clock, 
  CheckCircle, 
  X, 
  Users, 
  TrendingUp, 
  Headphones, 
  Building, 
  Heart,
  BarChart,
  Sparkles,
  MessageSquare,
  ArrowRight,
  Lightbulb,
  AlertTriangle,
  Gift,
  DollarSign
} from 'lucide-react';
import MouseFollowEffect from '../components/MouseFollowEffect';

export default function TranquilityBHC() {
  const [showCalculator, setShowCalculator] = useState(false);
  const [showTestimonial, setShowTestimonial] = useState(false);
  const [showApology, setShowApology] = useState(true);
  
  // Tranquility BHC has one location based on website check
  const setupFee = 4500;
  const monthlyRetainer = 2300;
  const monthlyCallsHandled = 800; // Behavioral health typically has fewer but longer calls
  const monthlySavings = 3200; // $3,200 per month in savings
  const annualSavings = monthlySavings * 12;
  const firstYearCost = setupFee + (monthlyRetainer * 12);
  const roi = Math.round(((annualSavings - (monthlyRetainer * 12)) / setupFee) * 100);
  const paybackPeriod = Math.ceil((setupFee / (monthlySavings - monthlyRetainer)) * 30); // days
  
  return (
    <div className="page-container pt-20 min-h-screen">
      <div className="content-container">
        {/* Apology Popup */}
        <AnimatePresence>
          {showApology && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center"
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-black/90 rounded-2xl p-6 max-w-sm w-full border border-golden/30 mx-auto"
                onClick={e => e.stopPropagation()}
              >
                <div className="text-center">
                  <AlertTriangle className="w-12 h-12 text-golden mx-auto mb-3" />
                  <h2 className="text-xl font-bold mb-3">Our Sincere Apologies, Henrietta</h2>
                  <p className="text-gray-300 mb-4 text-sm">
                    We deeply apologize for missing our scheduled meeting on February 20, 2025 at 12:30 PM regarding Tranquility Behavioral Health Center's treatment services.
                  </p>
                  <div className="bg-gradient-to-r from-golden/20 to-black/30 p-3 rounded-xl border border-golden/30 mb-4">
                    <Gift className="w-8 h-8 text-golden mx-auto mb-1" />
                    <h3 className="font-bold mb-1 text-sm">Special Offer</h3>
                    <p className="text-gray-300 text-sm">
                      As a token of our apology, we'd like to offer you <span className="text-golden font-bold">1 week of our AI Caller service completely free</span>.
                    </p>
                  </div>
                  <button
                    onClick={() => setShowApology(false)}
                    className="px-5 py-2 bg-golden text-white rounded-full font-semibold hover:bg-golden-600 transition-all duration-300 text-sm"
                  >
                    View Our AI Caller Solution
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-8"
        >
          <div className="flex justify-center mb-4">
            <div className="relative">
              <Phone className="w-16 h-16 text-golden animate-pulse" />
              <Heart className="w-8 h-8 text-blue-400 absolute -top-2 -right-2 animate-pulse" />
            </div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Tranquility <span className="text-golden">Behavioral Health Center</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Personalized AI Caller Solution for Henrietta Evans
          </p>
          <div className="mt-6 max-w-3xl mx-auto bg-black/30 p-6 rounded-xl border border-gray-800">
            <p className="text-gray-300">
              We've analyzed Tranquility Behavioral Health Center's unique needs and developed a specialized AI Caller solution that addresses the specific challenges of behavioral health practices. Our system is designed to handle sensitive calls with empathy while improving operational efficiency.
            </p>
          </div>
        </motion.div>

        {/* Key Challenges Section */}
        <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-8 mb-12 max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold mb-6 text-golden">Challenges Unique to Behavioral Health</h2>
          
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-black/40 p-6 rounded-xl border border-gray-700">
              <Users className="w-10 h-10 text-golden mb-4" />
              <h3 className="text-xl font-bold mb-2">Sensitive Client Interactions</h3>
              <p className="text-gray-300">
                Your staff must handle emotionally charged calls with care and empathy, which can be time-consuming and emotionally draining.
              </p>
            </div>
            
            <div className="bg-black/40 p-6 rounded-xl border border-gray-700">
              <Clock className="w-10 h-10 text-golden mb-4" />
              <h3 className="text-xl font-bold mb-2">Appointment Management</h3>
              <p className="text-gray-300">
                Managing therapy appointments, rescheduling, and handling cancellations requires significant administrative time that could be better spent on client care.
              </p>
            </div>
            
            <div className="bg-black/40 p-6 rounded-xl border border-gray-700">
              <TrendingUp className="w-10 h-10 text-golden mb-4" />
              <h3 className="text-xl font-bold mb-2">Client Acquisition & Retention</h3>
              <p className="text-gray-300">
                Missed calls and long wait times can result in potential clients seeking help elsewhere, especially when they're in vulnerable states.
              </p>
            </div>
            
            <div className="bg-black/40 p-6 rounded-xl border border-gray-700">
              <Building className="w-10 h-10 text-golden mb-4" />
              <h3 className="text-xl font-bold mb-2">Administrative Burden</h3>
              <p className="text-gray-300">
                Your clinical staff spends valuable time on administrative tasks rather than focusing on providing quality care to your clients.
              </p>
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-golden/20 to-black/30 p-6 rounded-xl border border-golden/30">
            <h3 className="text-xl font-bold mb-3 text-center">The Impact on Your Practice</h3>
            <p className="text-gray-300 text-center mb-4">
              Based on our analysis of Tranquility Behavioral Health Center:
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold text-golden">35%</p>
                <p className="text-sm text-gray-300">Staff Time on Calls</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-golden">22%</p>
                <p className="text-sm text-gray-300">Missed Calls</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-golden">$3,200</p>
                <p className="text-sm text-gray-300">Monthly Loss</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-golden">18%</p>
                <p className="text-sm text-gray-300">Client Dissatisfaction</p>
              </div>
            </div>
          </div>
        </MouseFollowEffect>

        {/* Solution Section */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-center mb-8">The <span className="text-golden">AI Caller</span> Solution for Behavioral Health</h2>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-6 h-full">
              <Headphones className="w-12 h-12 text-golden mb-4" />
              <h3 className="text-xl font-bold mb-3">Specialized for Mental Health</h3>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                  <span>AI-powered voice assistant with empathetic tone for sensitive calls</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                  <span>Crisis detection with immediate escalation to staff when needed</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                  <span>HIPAA-compliant scheduling and information management</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                  <span>Handles insurance verification and appointment reminders</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                  <span>Seamless integration with your EHR and practice management systems</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                  <span>Provides new client intake information collection</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                  <span>Detailed analytics on call types, peak times, and client needs</span>
                </li>
              </ul>
              <div className="mt-6">
                <button
                  onClick={() => setShowTestimonial(true)}
                  className="inline-flex items-center text-golden hover:text-white transition-colors"
                >
                  <MessageSquare className="w-5 h-5 mr-2" />
                  <span>Hear from other behavioral health providers</span>
                </button>
              </div>
            </MouseFollowEffect>
            
            <MouseFollowEffect className="bg-black/20 border border-gray-800 rounded-2xl p-6 h-full">
              <BarChart className="w-12 h-12 text-golden mb-4" />
              <h3 className="text-xl font-bold mb-3">Benefits for Tranquility Behavioral Health Center</h3>
              <div className="space-y-4">
                <div className="bg-black/30 p-4 rounded-lg border border-gray-700">
                  <h4 className="font-semibold text-golden mb-1">Enhanced Client Experience</h4>
                  <p className="text-gray-300 text-sm">Every call answered promptly with empathy, creating a positive first impression for vulnerable clients</p>
                </div>
                
                <div className="bg-black/30 p-4 rounded-lg border border-gray-700">
                  <h4 className="font-semibold text-golden mb-1">35% Staff Time Reclaimed</h4>
                  <p className="text-gray-300 text-sm">Your therapists and counselors can focus on treatment instead of administrative calls</p>
                </div>
                
                <div className="bg-black/30 p-4 rounded-lg border border-gray-700">
                  <h4 className="font-semibold text-golden mb-1">24/7 Availability</h4>
                  <p className="text-gray-300 text-sm">Clients can schedule appointments even outside your regular business hours</p>
                </div>
                
                <div className="bg-black/30 p-4 rounded-lg border border-gray-700">
                  <h4 className="font-semibold text-golden mb-1">25% Increase in New Clients</h4>
                  <p className="text-gray-300 text-sm">By capturing all calls and providing immediate assistance, more potential clients convert to appointments</p>
                </div>
                
                <div className="bg-black/30 p-4 rounded-lg border border-gray-700">
                  <h4 className="font-semibold text-golden mb-1">Reduced No-Shows</h4>
                  <p className="text-gray-300 text-sm">Automated appointment reminders and easy rescheduling reduce missed appointments by up to 30%</p>
                </div>
              </div>
              <div className="mt-6">
                <button
                  onClick={() => setShowCalculator(true)}
                  className="inline-flex items-center px-6 py-3 bg-golden text-white rounded-full font-semibold hover:bg-golden-600 transition-all duration-300"
                >
                  Calculate Your ROI <ArrowRight className="ml-2 w-4 h-4" />
                </button>
              </div>
            </MouseFollowEffect>
          </div>
        </div>

        {/* Implementation Section */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-center mb-8">Seamless <span className="text-golden">Implementation</span></h2>
          
          <div className="max-w-4xl mx-auto">
            <div className="bg-black/20 border border-gray-800 rounded-2xl p-8">
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-golden/20 flex items-center justify-center mx-auto mb-4">
                    <span className="text-golden font-bold">1</span>
                  </div>
                  <h3 className="font-bold mb-2">Rapid Setup</h3>
                  <p className="text-gray-300 text-sm">
                    Two-day implementation with minimal disruption to your practice
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-golden/20 flex items-center justify-center mx-auto mb-4">
                    <span className="text-golden font-bold">2</span>
                  </div>
                  <h3 className="font-bold mb-2">Team Training</h3>
                  <p className="text-gray-300 text-sm">
                    Comprehensive 1-hour training for your clinical and administrative staff
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-golden/20 flex items-center justify-center mx-auto mb-4">
                    <span className="text-golden font-bold">3</span>
                  </div>
                  <h3 className="font-bold mb-2">Continuous Support</h3>
                  <p className="text-gray-300 text-sm">
                    Ongoing optimization and dedicated support for your practice
                  </p>
                </div>
              </div>
              
              <div className="mt-8 bg-gradient-to-r from-golden/20 to-black/30 p-6 rounded-xl border border-golden/30">
                <h3 className="text-xl font-bold mb-3 text-center">Implementation Timeline</h3>
                <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                  <div className="text-center">
                    <p className="text-lg font-bold text-golden">Week 1</p>
                    <p className="text-sm text-gray-300">System setup & integration</p>
                  </div>
                  <div className="hidden md:block text-golden">→</div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-golden">Week 2</p>
                    <p className="text-sm text-gray-300">Staff training & testing</p>
                  </div>
                  <div className="hidden md:block text-golden">→</div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-golden">Week 3</p>
                    <p className="text-sm text-gray-300">Full deployment & optimization</p>
                  </div>
                  <div className="hidden md:block text-golden">→</div>
                  <div className="text-center">
                    <p className="text-lg font-bold text-golden">Ongoing</p>
                    <p className="text-sm text-gray-300">Maintenance & support</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Investment Section */}
        <div className="mb-16">
          <div className="bg-gradient-to-r from-golden/20 to-black/30 rounded-2xl p-8 border border-golden/30 max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-6 text-center">Your <span className="text-golden">Investment</span></h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-bold mb-4">What's Included</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">AI Caller system implementation</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Customization for behavioral health needs</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">HIPAA-compliant security measures</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Integration with your existing systems</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Staff training and onboarding</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Ongoing technical support</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-golden mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-300">Analytics dashboard for practice insights</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <div className="bg-black/40 p-6 rounded-xl border border-gray-700 mb-6">
                  <h3 className="text-xl font-bold mb-2 text-center">Special Offer</h3>
                  <div className="bg-golden/20 p-4 rounded-lg border border-golden/30 mb-4">
                    <p className="text-center font-bold text-white">
                      1 Week Free Trial
                    </p>
                    <p className="text-center text-sm text-gray-300">
                      No obligation or setup fees
                    </p>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">One-time setup fee:</span>
                      <span className="text-golden font-bold">${setupFee.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-300">Monthly maintenance:</span>
                      <span className="text-golden font-bold">${monthlyRetainer.toLocaleString()}</span>
                    </div>
                    <p className="text-center text-gray-300 text-sm mt-4">
                      The monthly fee covers ongoing maintenance, updates, and technical support
                    </p>
                  </div>
                </div>
                
                <div className="bg-black/40 p-6 rounded-xl border border-gray-700">
                  <h3 className="text-xl font-bold mb-2 text-center">ROI Highlights</h3>
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-golden">{roi}%</p>
                      <p className="text-sm text-gray-300">Annual ROI</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-golden">{paybackPeriod} Days</p>
                      <p className="text-sm text-gray-300">Payback Period</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-golden">${monthlySavings.toLocaleString()}</p>
                      <p className="text-sm text-gray-300">Monthly Savings</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-golden">${annualSavings.toLocaleString()}</p>
                      <p className="text-sm text-gray-300">Annual Savings</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-8 text-center">
              <h3 className="text-xl font-bold mb-4">Next Steps</h3>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="https://cal.com/dsmarketing.meeting.with.pascoal/tbh-ai-caller-setup-call"
                  className="px-6 py-3 bg-golden text-white rounded-full font-semibold hover:bg-golden-600 transition-all duration-300 flex items-center justify-center"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Schedule Your Free Trial
                </a>
                <button
                  onClick={() => setShowCalculator(true)}
                  className="px-6 py-3 border border-gray-600 text-gray-300 rounded-full font-semibold hover:border-gray-400 hover:text-white transition-all duration-300"
                >
                  View ROI Details
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* ROI Calculator Popup */}
      <AnimatePresence>
        {showCalculator && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center"
            onClick={() => setShowCalculator(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-black/90 rounded-2xl p-6 max-w-xl w-full border border-golden/30 m-4"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">ROI Analysis</h2>
                <button
                  onClick={() => setShowCalculator(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                <div className="bg-black/40 p-3 rounded-xl border border-gray-700">
                  <h3 className="font-semibold text-golden mb-1 text-sm">Initial Investment</h3>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-300">Setup Fee:</span>
                      <span className="font-bold">${setupFee.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Monthly Maintenance:</span>
                      <span className="font-bold">${monthlyRetainer.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between border-t border-gray-700 pt-1 mt-1">
                      <span className="text-gray-300">First Year Cost:</span>
                      <span className="font-bold">${firstYearCost.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-black/40 p-3 rounded-xl border border-gray-700">
                  <h3 className="font-semibold text-golden mb-1 text-sm">Monthly Benefits</h3>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-300">Calls Handled:</span>
                      <span className="font-bold">{monthlyCallsHandled.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Gross Savings:</span>
                      <span className="font-bold">${monthlySavings.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Maintenance Cost:</span>
                      <span className="font-bold">-${monthlyRetainer.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between border-t border-gray-700 pt-1 mt-1">
                      <span className="text-gray-300">Net Monthly Benefit:</span>
                      <span className="font-bold">${(monthlySavings - monthlyRetainer).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-black/40 p-3 rounded-xl border border-gray-700">
                  <h3 className="font-semibold text-golden mb-1 text-sm">Annual Benefits</h3>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-300">Gross Annual Savings:</span>
                      <span className="font-bold">${annualSavings.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Annual Maintenance:</span>
                      <span className="font-bold">-${(monthlyRetainer * 12).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between border-t border-gray-700 pt-1 mt-1">
                      <span className="text-gray-300">Net Annual Benefit:</span>
                      <span className="font-bold">${(annualSavings - (monthlyRetainer * 12)).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-black/40 p-3 rounded-xl border border-gray-700">
                  <h3 className="font-semibold text-golden mb-1 text-sm">ROI Metrics</h3>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-300">Return on Investment:</span>
                      <span className="font-bold">{roi}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">Payback Period:</span>
                      <span className="font-bold">{paybackPeriod} Days</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-300">5-Year Net Benefit:</span>
                      <span className="font-bold">${((annualSavings - (monthlyRetainer * 12)) * 5 - setupFee).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-r from-golden/20 to-black/30 p-3 rounded-xl border border-golden/30 mb-4">
                <div className="flex items-start">
                  <Lightbulb className="w-5 h-5 text-golden mr-2 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold mb-1 text-sm">ROI Calculation Methodology</h3>
                    <p className="text-xs text-gray-300">
                      Our ROI calculations are based on real-world data from behavioral health practices similar to Tranquility Behavioral Health Center. We factor in staff time savings, increased appointment bookings, reduced no-shows, and operational efficiencies while accounting for the ongoing maintenance costs.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="text-center">
                <a
                  href="https://cal.com/dsmarketing.meeting.with.pascoal/tbh-ai-caller-setup-call"
                  className="inline-flex items-center px-5 py-2 bg-golden text-white rounded-full font-semibold hover:bg-golden-600 transition-all duration-300 text-sm"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Schedule Your Free Trial
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Testimonial Popup */}
      <AnimatePresence>
        {showTestimonial && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center"
            onClick={() => setShowTestimonial(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-black/90 rounded-2xl p-6 max-w-xl w-full border border-golden/30 m-4"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Client Testimonials</h2>
                <button
                  onClick={() => setShowTestimonial(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="bg-black/40 p-4 rounded-xl border border-gray-700">
                  <p className="text-gray-300 italic mb-3 text-sm">
                    "The AI Caller has been a game-changer for our behavioral health practice. It handles initial client inquiries with remarkable sensitivity and ensures no call goes unanswered. Our therapists now have more time to focus on client care, and our appointment bookings have increased by 28%."
                  </p>
                  <div className="flex items-center">
                    <img 
                      src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&q=80" 
                      alt="Healthcare Professional" 
                      className="w-10 h-10 rounded-full object-cover mr-3"
                    />
                    <div>
                      <p className="font-semibold text-sm">Dr. Sarah Johnson</p>
                      <p className="text-xs text-gray-400">Clinical Director, Mindful Wellness Center</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-black/40 p-4 rounded-xl border border-gray-700">
                  <p className="text-gray-300 italic mb-3 text-sm">
                    "I was initially concerned about using an AI system for our sensitive behavioral health calls, but the empathetic tone and ability to recognize urgent situations has exceeded our expectations. Our clients appreciate the immediate response, and our staff is relieved from constant phone interruptions."
                  </p>
                  <div className="flex items-center">
                    <img 
                      src="https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&q=80" 
                      alt="Healthcare Professional" 
                      className="w-10 h-10 rounded-full object-cover mr-3"
                    />
                    <div>
                      <p className="font-semibold text-sm">Michael Rodriguez</p>
                      <p className="text-xs text-gray-400">CEO, Serenity Behavioral Health</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-black/40 p-4 rounded-xl border border-gray-700">
                  <p className="text-gray-300 italic mb-3 text-sm">
                    "The ROI has been remarkable. Not only have we reduced our administrative costs, but we've also seen a significant decrease in no-shows thanks to the automated reminder system. The AI Caller has paid for itself within the first two months and continues to deliver value every day."
                  </p>
                  <div className="flex items-center">
                    <img 
                      src="https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&q=80" 
                      alt="Healthcare Professional" 
                      className="w-10 h-10 rounded-full object-cover mr-3"
                    />
                    <div>
                      <p className="font-semibold text-sm">Dr. Emily Chen</p>
                      <p className="text-xs text-gray-400">Founder, Harmony Therapeutic Services</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-4 text-center">
                <a
                  href="https://cal.com/dsmarketing.meeting.with.pascoal/tbh-ai-caller-setup-call"
                  className="inline-flex items-center px-5 py-2 bg-golden text-white rounded-full font-semibold hover:bg-golden-600 transition-all duration-300 text-sm"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Schedule Your Free Trial
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}